import { create } from 'zustand';
import {
  cognitiveApi,
  graphVizApi,
  type CognitiveClient,
  type SearchResult,
  type GraphSnapshot,
  type GraphVizStats,
  type GraphNode,
  type LayoutMode,
  type ProjectionMethod,
  type SnapshotRequest,
} from '@/services/api';

export interface GraphFilters {
  clientId: string | null;
  layout: LayoutMode;
  projection: ProjectionMethod;
  minEdgeSimilarity: number;
  includeStructural: boolean;
  entityClasses: string[] | null;
  maxNodes: number;
}

const DEFAULT_FILTERS: GraphFilters = {
  clientId: null,
  layout: 'force',
  projection: 'umap',
  minEdgeSimilarity: 0.55,
  includeStructural: true,
  entityClasses: null,
  maxNodes: 2000,
};

interface GraphState {
  clients: CognitiveClient[];
  searchResults: SearchResult[];
  searchQuery: string;

  snapshot: GraphSnapshot | null;
  stats: GraphVizStats | null;
  filters: GraphFilters;

  // Node interaction
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  highlightedNodeIds: Set<string>;
  navigationHistory: string[];

  // UI state
  selectedClient: string | null;
  loading: boolean;
  vizLoading: boolean;
  error: string | null;
  sidebarCollapsed: boolean;
  detailPanelOpen: boolean;
  fullscreen: boolean;

  // Actions — cognitive
  fetchClients: () => Promise<void>;
  search: (query: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;

  // Actions — visualization
  loadSnapshot: (overrides?: Partial<GraphFilters>) => Promise<void>;
  loadStats: () => Promise<void>;
  updateFilters: (partial: Partial<GraphFilters>) => void;
  setSelectedClient: (uid: string | null) => void;
  reload: () => Promise<void>;

  // Actions — node interaction
  selectNode: (nodeId: string | null) => void;
  hoverNode: (nodeId: string | null) => void;
  setHighlightedNodes: (ids: Set<string>) => void;
  navigateToNode: (nodeId: string) => void;
  goBack: () => void;

  // Actions — neighborhood expansion
  expandNeighborhood: (nodeId: string, depth?: number) => Promise<void>;

  // Actions — deep link focus (scenario → graph exploration jump-off)
  focusOnNode: (clientUid: string, nodeId: string) => Promise<void>;

  // Actions — UI
  toggleSidebar: () => void;
  toggleFullscreen: () => void;
  setFullscreen: (v: boolean) => void;

  // Derived
  getSelectedNode: () => GraphNode | null;
}

export const useGraphStore = create<GraphState>((set, get) => ({
  clients: [],
  searchResults: [],
  searchQuery: '',
  snapshot: null,
  stats: null,
  filters: { ...DEFAULT_FILTERS },
  selectedNodeId: null,
  hoveredNodeId: null,
  highlightedNodeIds: new Set(),
  navigationHistory: [],
  selectedClient: null,
  loading: false,
  vizLoading: false,
  error: null,
  sidebarCollapsed: false,
  detailPanelOpen: false,
  fullscreen: false,

  // -----------------------------------------------------------------------
  // Cognitive
  // -----------------------------------------------------------------------

  fetchClients: async () => {
    set({ loading: true, error: null });
    try {
      const { clients } = await cognitiveApi.listClients();
      set({ clients });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch clients' });
    } finally {
      set({ loading: false });
    }
  },

  search: async (query) => {
    if (!query.trim()) {
      set({ searchResults: [], searchQuery: '' });
      return;
    }
    set({ loading: true, searchQuery: query, error: null });
    try {
      const { results } = await cognitiveApi.search(query);
      set({ searchResults: results });

      const nodeIds = new Set(results.map((r) => r.id));
      set({ highlightedNodeIds: nodeIds });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Search failed' });
    } finally {
      set({ loading: false });
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),
  clearSearch: () =>
    set({ searchResults: [], searchQuery: '', highlightedNodeIds: new Set() }),

  // -----------------------------------------------------------------------
  // Visualization
  // -----------------------------------------------------------------------

  loadSnapshot: async (overrides) => {
    const current = get().filters;
    const filters = overrides ? { ...current, ...overrides } : current;

    set({ vizLoading: true, error: null, filters });

    try {
      const req: SnapshotRequest = {
        client_id: filters.clientId,
        layout: filters.layout,
        projection: filters.projection,
        min_edge_similarity: filters.minEdgeSimilarity,
        include_structural: filters.includeStructural,
        entity_classes: filters.entityClasses,
        max_nodes: filters.maxNodes,
      };
      const snapshot = await graphVizApi.snapshot(req);
      set({ snapshot, vizLoading: false });
    } catch (e) {
      set({
        vizLoading: false,
        error: e instanceof Error ? e.message : 'Failed to load graph',
      });
    }
  },

  loadStats: async () => {
    try {
      const stats = await graphVizApi.stats();
      set({ stats });
    } catch {
      // non-critical
    }
  },

  updateFilters: (partial) => {
    const current = get().filters;
    const next = { ...current, ...partial };
    set({ filters: next });
    get().loadSnapshot(next);
  },

  setSelectedClient: (uid) => {
    set({ selectedClient: uid });
    if (uid) {
      get().updateFilters({ clientId: uid });
    }
  },

  reload: async () => {
    await Promise.all([get().loadSnapshot(), get().loadStats()]);
  },

  // -----------------------------------------------------------------------
  // Node interaction
  // -----------------------------------------------------------------------

  selectNode: (nodeId) => {
    set({
      selectedNodeId: nodeId,
      detailPanelOpen: nodeId !== null,
    });
  },

  hoverNode: (nodeId) => {
    set({ hoveredNodeId: nodeId });
  },

  setHighlightedNodes: (ids) => {
    set({ highlightedNodeIds: ids });
  },

  navigateToNode: (nodeId) => {
    const prev = get().selectedNodeId;
    const history = get().navigationHistory;
    set({
      selectedNodeId: nodeId,
      detailPanelOpen: true,
      navigationHistory: prev ? [...history, prev] : history,
    });
  },

  goBack: () => {
    const history = [...get().navigationHistory];
    const prev = history.pop();
    set({
      selectedNodeId: prev ?? null,
      detailPanelOpen: prev != null,
      navigationHistory: history,
    });
  },

  focusOnNode: async (clientUid, nodeId) => {
    // Load the client-scoped snapshot (all entity classes) so we have a
    // base graph to merge the neighborhood into.
    set({ selectedClient: clientUid });
    await get().loadSnapshot({
      clientId: clientUid,
      entityClasses: null,
      maxNodes: 3000,
    });

    // Expand the neighborhood around the focus node so linked entities
    // (keyword intents / foundation queries / factors / personas) are
    // guaranteed to be present even if the base snapshot was capped.
    try {
      await get().expandNeighborhood(nodeId, 1);
    } catch {
      // non-critical — the base snapshot may already contain the node
    }

    // Open the detail panel on the focus node and seed the navigation
    // history so "Back" returns to the base view.
    set({
      selectedNodeId: nodeId,
      detailPanelOpen: true,
      navigationHistory: [],
    });
  },

  expandNeighborhood: async (nodeId, depth = 1) => {
    const { snapshot } = get();
    if (!snapshot) return;

    set({ vizLoading: true });
    try {
      const neighborhood = await graphVizApi.neighborhood(
        nodeId,
        depth,
        get().filters.minEdgeSimilarity,
      );

      const existingNodeIds = new Set(snapshot.nodes.map((n) => n.id));
      const existingEdgeKeys = new Set(
        snapshot.links.map((l) => `${l.source}-${l.target}`),
      );

      const newNodes = neighborhood.nodes.filter((n) => !existingNodeIds.has(n.id));
      const newLinks = neighborhood.links.filter(
        (l) => !existingEdgeKeys.has(`${l.source}-${l.target}`) &&
               !existingEdgeKeys.has(`${l.target}-${l.source}`),
      );

      const mergedSnapshot: GraphSnapshot = {
        nodes: [...snapshot.nodes, ...newNodes],
        links: [...snapshot.links, ...newLinks],
        meta: {
          ...snapshot.meta,
          node_count: snapshot.meta.node_count + newNodes.length,
          link_count: snapshot.meta.link_count + newLinks.length,
        },
      };

      set({ snapshot: mergedSnapshot, vizLoading: false });
    } catch (e) {
      set({
        vizLoading: false,
        error: e instanceof Error ? e.message : 'Neighborhood expansion failed',
      });
    }
  },

  // -----------------------------------------------------------------------
  // UI
  // -----------------------------------------------------------------------

  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  toggleFullscreen: () => set((s) => ({ fullscreen: !s.fullscreen })),
  setFullscreen: (v) => set({ fullscreen: v }),

  // -----------------------------------------------------------------------
  // Derived
  // -----------------------------------------------------------------------

  getSelectedNode: () => {
    const { snapshot, selectedNodeId } = get();
    if (!snapshot || !selectedNodeId) return null;
    return snapshot.nodes.find((n) => n.id === selectedNodeId) ?? null;
  },
}));
